// Parses a standardised Alvora template (row 1 headers, data from row 2).
import * as XLSX from 'xlsx';
import { IMPORT_TYPES, TENDER_COMPONENTS } from './templates';

const TOL = 0.5;
const COST_CODE_RE = /^[A-Za-z0-9][A-Za-z0-9.\-/ ]{0,29}$/;

const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/[^a-z0-9€]+/g, ' ').trim();
const round2 = (n) => Math.round(n * 100) / 100;

export function toNumber(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  if (!s || s === '-' || s === '—') return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1); }
  s = s.replace(/[€,\s]/g, '');
  if (s.startsWith('-')) { neg = !neg; s = s.slice(1); }
  if (!/^\d*\.?\d+$/.test(s)) return NaN;
  const n = parseFloat(s);
  return neg ? -n : n;
}

export function matchComponent(text) {
  const t = norm(text);
  if (!t) return null;
  // Check specific components before the generic 'measured works' fallback.
  const order = ['tender_total', 'vat', 'preliminaries', 'provisional_sums', 'contingencies', 'adjustments', 'measured_works'];
  for (const key of order) {
    const c = TENDER_COMPONENTS.find((x) => x.key === key);
    if (c.match.test(t)) return key;
  }
  return null;
}

function mapHeaders(headerCells, columns) {
  const map = {};
  const unknown = [];
  headerCells.forEach((h, idx) => {
    const n = norm(h);
    if (!n) return;
    const col = columns.find((c) => norm(c.header) === n || (c.aliases || []).some((a) => norm(a) === n));
    if (col && map[col.key] === undefined) map[col.key] = idx;
    else unknown.push(String(h));
  });
  return { map, unknown };
}

export async function sha256File(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function parseTemplate(file, typeKey) {
  const t = IMPORT_TYPES[typeKey];
  if (!/\.xlsx$/i.test(file.name || '')) throw new Error('Please upload a .xlsx file saved from the Alvora template.');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) throw new Error('The workbook has no worksheets.');
  const grid = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: '', raw: true, blankrows: false });
  if (!grid.length) throw new Error('The first worksheet is empty.');

  const { map, unknown } = mapHeaders(grid[0], t.columns);
  const missing = t.columns.filter((c) => c.required && map[c.key] === undefined).map((c) => c.header);
  if (missing.length) {
    throw new Error(`Row 1 is missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Download the template and keep its headings.`);
  }

  const errors = [];
  const warnings = [];
  if (unknown.length) warnings.push({ row: 1, message: `Ignored unrecognised column${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}` });

  const rows = [];
  const seenRefs = new Map();
  for (let i = 1; i < grid.length; i++) {
    const cells = grid[i] || [];
    const excelRow = i + 1;
    const get = (key) => (map[key] === undefined ? '' : cells[map[key]]);
    if (t.columns.every((c) => String(get(c.key) ?? '').trim() === '')) continue;

    const row = { source_row: excelRow };
    const err = (m) => errors.push({ row: excelRow, message: m });
    for (const c of t.columns) {
      const raw = get(c.key);
      if (c.type === 'number') {
        const n = toNumber(raw);
        if (Number.isNaN(n)) err(`${c.header} "${raw}" is not a number`);
        row[c.key] = Number.isNaN(n) ? null : n;
      } else if (c.type === 'component') {
        row.component_label = String(raw ?? '').trim();
        row.component = matchComponent(raw);
        if (!row.component) err(`Component "${raw}" not recognised. Use: ${TENDER_COMPONENTS.map((x) => x.label).join(', ')}`);
      } else {
        row[c.key] = String(raw ?? '').trim() || null;
      }
      if (c.required && c.type !== 'component' && (row[c.key] == null || row[c.key] === '') && !(c.type === 'number' && c.key === t.totalKey)) {
        err(`${c.header} is required`);
      }
    }
    validateRow(typeKey, row, err, (m) => warnings.push({ row: excelRow, message: m }), seenRefs);
    rows.push(row);
  }

  if (!rows.length) errors.push({ row: null, message: 'No data rows found below the header row.' });

  const summary = summarise(typeKey, rows, errors, warnings);
  return { import_type: typeKey, sheet_name: sheetName, rows, errors, warnings, ...summary, file_name: file.name, file_size_bytes: file.size };
}

function validateRow(typeKey, row, err, warn, seenRefs) {
  if (typeKey === 'priced_boq' || typeKey === 'cost_budget') {
    if (row.cost_code && !COST_CODE_RE.test(row.cost_code)) err(`Cost Code "${row.cost_code}" is invalid (letters, digits, . - / only, max 30)`);
    if (row.item_ref) {
      if (seenRefs.has(row.item_ref)) warn(`Item Ref "${row.item_ref}" also appears on row ${seenRefs.get(row.item_ref)}`);
      else seenRefs.set(row.item_ref, row.source_row);
    }
  }
  if (typeKey === 'priced_boq') {
    const { quantity: q, selling_rate: r } = row;
    if (q != null && q < 0) err('Quantity cannot be negative');
    if (q != null && r != null) {
      const calc = round2(q * r);
      if (row.selling_total == null) row.selling_total = calc;
      else if (Math.abs(calc - row.selling_total) > 0.01) err(`Selling Total ${row.selling_total.toFixed(2)} ≠ Quantity × Rate (${calc.toFixed(2)})`);
    }
    if (row.selling_total == null) err('Selling Total is required (or supply Quantity and Selling Rate)');
    else row.selling_total = round2(row.selling_total);
    if ((q != null) !== (r != null)) warn('Only one of Quantity / Selling Rate supplied');
  }
  if (typeKey === 'cost_budget') {
    const parts = ['labour', 'materials', 'subcontractor', 'plant', 'other'];
    const any = parts.some((k) => row[k] != null);
    parts.forEach((k) => { row[k] = round2(row[k] ?? 0); });
    const sum = round2(parts.reduce((a, k) => a + row[k], 0));
    if (!any && row.total_cost == null) err('At least one cost column (or Total Cost) is required');
    if (row.total_cost == null) row.total_cost = sum;
    else if (any && Math.abs(sum - row.total_cost) > 0.01) err(`Total Cost ${row.total_cost.toFixed(2)} ≠ sum of cost columns (${sum.toFixed(2)})`);
    row.total_cost = round2(row.total_cost);
  }
  if (typeKey === 'tender_summary') {
    if (row.amount == null) err('Amount is required');
    else row.amount = round2(row.amount);
  }
}

function summarise(typeKey, rows, errors, warnings) {
  const valid = rows;
  if (typeKey === 'tender_summary') {
    const by = (k) => valid.filter((r) => r.component === k);
    const sumOf = (list) => round2(list.reduce((a, r) => a + (r.amount || 0), 0));
    const totalLines = by('tender_total');
    const vatLines = by('vat');
    const sub = sumOf(valid.filter((r) => r.component && r.component !== 'vat' && r.component !== 'tender_total'));
    const vat = sumOf(vatLines);
    const total = sumOf(totalLines);
    if (totalLines.length !== 1) errors.push({ row: null, message: `Exactly one Tender Total line is required (found ${totalLines.length})` });
    if (vatLines.length > 1) errors.push({ row: null, message: 'Only one VAT line is allowed' });
    const arithmetic = round2(sub + vat - total);
    const arithmeticOk = Math.abs(arithmetic) <= TOL;
    if (!arithmeticOk && totalLines.length === 1) warnings.push({ row: null, message: `Components (€${sub.toFixed(2)}) + VAT (€${vat.toFixed(2)}) ≠ Tender Total (€${total.toFixed(2)}) — difference €${arithmetic.toFixed(2)}` });
    const vatRate = sub ? round2((vat / sub) * 10000) / 100 : null;
    return { computed_total: sub, tender_subtotal_ex_vat: sub, tender_vat_amount: vat, tender_total_inc_vat: total, vat_rate: vatRate, arithmetic_ok: arithmeticOk, arithmetic_variance: arithmetic };
  }
  const key = IMPORT_TYPES[typeKey].totalKey;
  const computed_total = round2(valid.reduce((a, r) => a + (r[key] || 0), 0));
  const byTrade = {};
  for (const r of valid) byTrade[r.trade || '—'] = round2((byTrade[r.trade || '—'] || 0) + (r[key] || 0));
  return { computed_total, by_trade: byTrade };
}

// Same rule as confirm_estimate_import(): |computed - expected| <= €0.50.
export function reconcile(parsed, expectedTotal) {
  const value = parsed.import_type === 'tender_summary' ? parsed.tender_total_inc_vat : parsed.computed_total;
  const variance = expectedTotal == null ? null : round2(value - expectedTotal);
  const failures = [];
  if (parsed.import_type === 'tender_summary') {
    if (!parsed.arithmetic_ok) failures.push('components_plus_vat_vs_total');
    if (expectedTotal != null && Math.abs(variance) > TOL) failures.push('tender_total_vs_expected');
  } else if (parsed.import_type === 'priced_boq') {
    if (expectedTotal == null) failures.push('no_expected_total');
    else if (Math.abs(variance) > TOL) failures.push('boq_total_vs_expected');
  } else if (expectedTotal != null && Math.abs(variance) > TOL) {
    failures.push('cost_total_vs_expected');
  }
  return { value, expected: expectedTotal, variance, failures, ok: failures.length === 0, tolerance: TOL };
}
